/**
 * The data behind Ingest's one job picker (IngestJobPicker.jsx): the slim
 * job list from the listIngestJobs callable, Pin / Archive through
 * setIngestListState (functions/src/services/ingestList.ts), and the job each
 * person last picked on each screen (this browser only).
 *
 * The list is kept for the tab, per account, so leaving a screen and coming
 * back shows it at once; it is read again in the background when it is more
 * than a minute old, every 5 minutes while a picker is open, and when the
 * window regains focus. Another account signing in never sees it.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { auth, callable, db, doc, getDocFromServer } from "../firebase.js";

const LIST_OPTIONS = { timeout: 45000 };
const SET_OPTIONS = { timeout: 20000 };

export const ingestList = {
  /** { now, canEdit, jobs: [slim job] } */
  list: async () => (await callable("listIngestJobs", LIST_OPTIONS)({})).data,
  /** changes: [{ jobId, mode: "auto" | "pin" | "archive" }] → { states: { [jobId]: state | null } } */
  setState: async (changes) => (await callable("setIngestListState", SET_OPTIONS)({ changes })).data,
};

export const STALE_MS = 60 * 1000;
export const POLL_MS = 5 * 60 * 1000;
const currentUid = () => auth?.currentUser?.uid ?? null;

let CACHE = null; // { uid, at, data }
let INFLIGHT = null; // { uid, promise }
// Pin / Archive changes made in this tab, for the account that made them:
//   PENDING  sent or waiting to be sent: shown over every list answer until
//            the server has answered (a read can never undo them meanwhile);
//   EDITS    settled (the server's answer, or the state put back after a
//            refusal), numbered: a list read that started before one answers
//            with the state from before it, so it is laid over that answer.
// The changes go to the server one at a time, in the order they were made,
// whichever picker made them (they outlive a picker that closes), and each
// job remembers its latest change: an answer to an earlier one is not shown
// over it. A change still waiting when the account changes is dropped.
let SEQ = 0;
const EDITS = new Map(); // jobId -> { list, seq, uid }
const PENDING = new Map(); // jobId -> { list, op, uid }
const LATEST = new Map(); // jobId -> op
let OPS = 0;
let QUEUE = Promise.resolve();
// The account session: a change made before the last clear (sign-out, another
// account) is never sent, nor its answer shown, even if the same account is
// back by then.
let GEN = 0;
// List reads, numbered as they start: an answer older than one already shown is dropped.
let READS = 0;
let SHOWN_READ = 0;
// Open pickers: each hears changes ({ states }) and accepted list answers ({ data }).
const LISTENERS = new Set();
// The last list loader a picker used, so a refused change can have the list read again
// even when the picker that made it has closed.
let LAST_LOAD = null;
export const clearIngestListCache = () => {
  CACHE = null; INFLIGHT = null; EDITS.clear(); PENDING.clear(); LATEST.clear();
  GEN += 1; SHOWN_READ = READS;
};

const applyStates = (data, states) => {
  if (!data || !Array.isArray(data.jobs)) return data;
  let changed = false;
  const jobs = data.jobs.map((j) => {
    if (!Object.prototype.hasOwnProperty.call(states, j.id)) return j;
    changed = true;
    return { ...j, list: states[j.id] };
  });
  return changed ? { ...data, jobs } : data;
};

/** The list with this account's settled edits made after `since`, and every pending change, laid over it. */
export function overlayEdits(data, since, uid) {
  const over = {};
  for (const [id, e] of EDITS) if (e.seq > since && e.uid === uid) over[id] = e.list;
  for (const [id, p] of PENDING) if (p.uid === uid) over[id] = p.list;
  return applyStates(data, over);
}

/** Shows states at once in every open picker (and the tab's kept list). */
function show(states) {
  const kept = CACHE && CACHE.uid === currentUid() ? CACHE : null;
  if (kept) CACHE = { ...kept, data: applyStates(kept.data, states) };
  for (const l of LISTENERS) l({ states });
}

/** After a refused change: read the list again now, and show the answer in every open picker. */
function rereadNow() {
  if (!LAST_LOAD) return;
  fetchList(LAST_LOAD, { fresh: true }).catch(() => {});
}

function recordEdits(states, uid) {
  SEQ += 1;
  for (const [id, list] of Object.entries(states)) EDITS.set(id, { list, seq: SEQ, uid });
}

/**
 * One change (a list of {jobId, mode}): shown at once as `shown`, sent after
 * the changes before it, then the server's answer shown, or `before` put
 * back if it was refused, for each job whose latest change it still is.
 * Resolves to { ok, current, error, aborted }: current is false when a later
 * change was made to one of its jobs (its answer or refusal is old news).
 */
export function mutateList(changes, { shown, before, send = ingestList.setState } = {}) {
  const uid = currentUid();
  const gen = GEN;
  const sameSession = () => GEN === gen && currentUid() === uid;
  const op = ++OPS;
  const ids = changes.map((c) => c.jobId);
  for (const id of ids) {
    LATEST.set(id, op);
    PENDING.set(id, { list: shown[id] ?? null, op, uid });
  }
  show(shown);
  const isLatest = (id) => LATEST.get(id) === op;
  // record: the server's answer is kept over older reads; a refusal's
  // rollback is only shown (a fresh read follows and is taken as it is).
  const settle = (states, record) => {
    const mine = {};
    for (const id of ids) {
      if (!isLatest(id)) continue;
      PENDING.delete(id);
      mine[id] = Object.prototype.hasOwnProperty.call(states, id) ? states[id] : (before[id] ?? null);
    }
    if (record) recordEdits(mine, uid);
    show(mine);
  };
  const run = QUEUE.then(async () => {
    if (!sameSession()) return { ok: false, current: false, aborted: true };
    try {
      const r = await send(changes);
      if (!sameSession()) return { ok: false, current: false, aborted: true };
      settle(r?.states ?? {}, true);
      return { ok: true, current: ids.every(isLatest) };
    } catch (error) {
      if (!sameSession()) return { ok: false, current: false, aborted: true };
      settle(before, false);
      const current = ids.every(isLatest);
      // Someone may have changed it meanwhile: read the list again (a read
      // started now) rather than trust the rollback.
      if (current) rereadNow();
      return { ok: false, current, error };
    }
  });
  QUEUE = run.catch(() => null);
  return run;
}

/**
 * One list read (a read already under way for this account is shared, unless
 * fresh: a read that must start now, after a refused change). Resolves to
 * { uid, data, stale }: stale when a read that started later was shown first.
 */
function fetchList(load, { fresh = false } = {}) {
  const uid = currentUid();
  if (!fresh && INFLIGHT && INFLIGHT.uid === uid && INFLIGHT.gen === GEN) return INFLIGHT.promise;
  const since = SEQ;
  const readNo = ++READS;
  const gen = GEN;
  const promise = load().then((answer) => {
    const data = overlayEdits(answer, since, uid);
    // An answer from before the session was cleared, for another account, or
    // older than one already shown: never shown, never kept.
    if (gen !== GEN || currentUid() !== uid || readNo < SHOWN_READ) return { uid, data, stale: true };
    SHOWN_READ = readNo;
    // Settled edits this answer already includes are no longer needed.
    for (const [id, e] of EDITS) if (e.seq <= since) EDITS.delete(id);
    CACHE = { uid, at: Date.now(), data };
    for (const l of LISTENERS) l({ data });
    return { uid, data, stale: false };
  }).finally(() => {
    if (INFLIGHT?.promise === promise) INFLIGHT = null;
  });
  INFLIGHT = { uid, gen, promise };
  return promise;
}

const cachedFor = (uid) => (CACHE && CACHE.uid === uid ? CACHE : null);

/**
 * { data, loading, error, reload }: data is { now, canEdit, jobs } or null;
 * error is set when the last read failed (data keeps the last good list, if
 * any). Changes made with mutateList show here at once.
 */
export function useIngestList({ load = ingestList.list } = {}) {
  const uid = currentUid();
  const first = cachedFor(uid);
  const [state, setState] = useState(() => ({ data: first ? overlayEdits(first.data, Infinity, uid) : null, loading: !first, error: null }));
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const reload = useCallback(async ({ quiet = false, fresh = false } = {}) => {
    LAST_LOAD = load;
    if (!quiet) setState((s) => ({ ...s, loading: !s.data, error: null }));
    try {
      // An accepted answer reaches every open picker through LISTENERS.
      const { stale } = await fetchList(load, { fresh });
      if (stale && alive.current) setState((s) => ({ ...s, loading: false }));
    } catch (error) {
      if (alive.current) setState((s) => ({ ...s, loading: false, error }));
    }
  }, [load]);

  useEffect(() => {
    const kept = cachedFor(currentUid());
    if (!kept || Date.now() - kept.at > STALE_MS) reload({ quiet: Boolean(kept) });
    const poll = setInterval(() => reload({ quiet: true }), POLL_MS);
    const onFocus = () => {
      const k = cachedFor(currentUid());
      if (!k || Date.now() - k.at > STALE_MS) reload({ quiet: true });
    };
    window.addEventListener("focus", onFocus);
    const listener = ({ states, data }) => setState((s) => {
      if (data) return { data, loading: false, error: null };
      return s.data ? { ...s, data: applyStates(s.data, states) } : s;
    });
    LISTENERS.add(listener);
    return () => { clearInterval(poll); window.removeEventListener("focus", onFocus); LISTENERS.delete(listener); };
  }, [reload]);

  return { ...state, reload };
}

/** A clock for the rule: now, moved on every minute. */
export function useMinuteClock() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60 * 1000);
    const tick = () => setNow(Date.now());
    window.addEventListener("focus", tick);
    return () => { clearInterval(t); window.removeEventListener("focus", tick); };
  }, []);
  return now;
}

/* ── the job each person last picked, per screen, in this browser ── */

/** A remembered job that cannot be opened: gone, or in the trash; unusable(data) adds a screen's own reasons. */
export const unusableJob = (data, unusable = null) => !data || Boolean(data.deletedAt) || Boolean(unusable?.(data));

/**
 * Whether the server itself says the remembered job cannot be opened. A read
 * that fails, or an answer from the browser's cache (offline), never does:
 * the job is only forgotten on the server's word.
 */
export async function jobUnusableOnServer(path, unusable = null) {
  try {
    const snap = await getDocFromServer(doc(db, path));
    return unusableJob(snap.exists() ? snap.data() : null, unusable);
  } catch {
    return false;
  }
}

/**
 * Forgets a job reopened from memory (onMissing) once the server confirms it
 * cannot be opened; nothing while it loads, on a read error, or while the
 * job read looks usable.
 */
export function useForgetIfUnusable({ path, data, loading, error, onMissing, unusable = null, check = jobUnusableOnServer }) {
  const looksUnusable = !loading && !error && unusableJob(data, unusable);
  useEffect(() => {
    if (!onMissing || !looksUnusable || !path) return undefined;
    let alive = true;
    check(path, unusable).then((gone) => { if (alive && gone) onMissing(); });
    return () => { alive = false; };
  }, [onMissing, looksUnusable, path, unusable, check]);
}

const KEY = (uid, screen) => `ingest.pickedJob.${screen}.${uid}`;

/** { jobId, open } or null. open: the job's screen was showing when they left. */
export function readPicked(screen, uid = currentUid()) {
  if (!uid) return null;
  try {
    const raw = window.localStorage.getItem(KEY(uid, screen));
    const v = raw ? JSON.parse(raw) : null;
    return v && typeof v.jobId === "string" && v.jobId ? { jobId: v.jobId, open: v.open === true } : null;
  } catch {
    return null;
  }
}

/** For the tests: the list read and the edit record, without React. */
export const __test = { fetchList, recordEdits, pending: PENDING };

export function writePicked(screen, value, uid = currentUid()) {
  if (!uid) return;
  try {
    if (value) window.localStorage.setItem(KEY(uid, screen), JSON.stringify({ jobId: value.jobId, open: Boolean(value.open) }));
    else window.localStorage.removeItem(KEY(uid, screen));
  } catch { /* private mode: nothing remembered */ }
}
